import React, { useState } from 'react';
import { TabletButton } from '@campfire/tablet-button';
import {
  Box,
  Grid,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Collapse,
} from '@material-ui/core';
import { StringParam, ArrayParam, useQueryParams } from 'use-query-params';
import { ProgramsContext } from '../../../../global/programs-sell/ProgramsContext';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FilterItem } from '../../../../common/filter-fields/FilterItem';
import { ActivityTagSelect } from '../../../general/activities-v2/ActivityFilter/ActivityTagSelect';
import { DaySelect } from '../../../general/activities-v2/ActivityFilter/DaySelect';
import * as ActivitiesConsts from '../../../general/activities-v2/ActivityConst';
import { sharedStyles } from '../../../general/activities-v2/ActivityStyles/shared';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GetAllActivityActivityTags } from '../../../general/activities-v2/ActivityQueries/__generated__/GetAllActivityActivityTags';
import { GET_ALL_ACTIVITY_ACTIVITY_TAGS } from '../../../general/activities-v2/ActivityQueries/get-activities-timeline.gql';

const activityTypes = [
  { id: 'all', name: 'All activity types' },
  { id: 'flexible', name: 'Flexible Activities' },
  { id: 'regular', name: 'Regular Activities' },
];

const activityStatuses = [
  { id: 'all', name: 'All activities' },
  { id: 'active', name: 'Active activities' },
  { id: 'suspended', name: 'Suspended activities' },
  { id: 'closed', name: 'Closed activities' },
  { id: 'ended', name: 'Ended activities' },
];

const rosterStatuses = [
  { id: 'all', name: 'All roster statuses' },
  { id: 'not_published', name: 'Not Published' },
  { id: 'action_required', name: 'Action Required' },
  { id: 'published', name: 'Published' },
  { id: 'cancelled', name: 'Cancelled' },
  { id: 'report-not-started', name: 'Report Not Started' },
  { id: 'report-missing', name: 'Report Missing' },
  { id: 'completed', name: 'Completed' },
];

interface CalendarRosterFilterProps {
  showFilters: boolean;
  onCollapseFilters?: () => void;
}

export function CalendarRosterFilter({ showFilters, onCollapseFilters }: CalendarRosterFilterProps) {
  const { theme, isMd, isMobile } = useCampfireTheme();
  const isXl = !useMediaQuery('(max-width:1565px)');
  const [query, setQuery] = useQueryParams({
    programId: StringParam,
    activityType: StringParam,
    activityStatus: StringParam,
    volunteersNeeded: StringParam,
    rosterType: StringParam,
    rosterStatus: StringParam,
    tags: ArrayParam,
    days: ArrayParam,
    pendingApproval: StringParam,
  });

  const [filters, setFilters] = useState({
    programId: query.programId || 'all',
    activityType: query.activityType || 'all',
    activityStatus: query.activityStatus || 'active',
    volunteersNeeded: query.volunteersNeeded === 'true',
    rosterType: query.rosterType || 'all',
    rosterStatus: query.rosterStatus || 'all',
    tags: query.tags || [],
    days: query.days || [],
    pendingApproval: query.pendingApproval === 'true',
  });

  const { getAuthorizedPrograms } = React.useContext(ProgramsContext);
  const programs = getAuthorizedPrograms();

  const { data: tagsData } = useCampfireQuery<GetAllActivityActivityTags, {}>(GET_ALL_ACTIVITY_ACTIVITY_TAGS);
  const activityTagOptions = React.useMemo(() => {
    return tagsData?.vm.activityTags ?? [];
  }, [tagsData]).sort((a, b) => a.name.localeCompare(b.name));

  const inputClasses = sharedStyles.textField();

  const handleChange = (filter: any) => {
    setFilters((prev) => ({
      ...prev,
      ...filter,
    }));
  };

  const onSelectDay = (dayValue: number) => {
    const dayString = dayValue.toString();
    setFilters((prev) => ({
      ...prev,
      days: prev.days?.includes(dayString)
        ? (prev.days || []).filter((day) => day !== dayString)
        : (prev.days || []).concat(dayString),
    }));
  };

  const onSelectActivityTag = (tagIds: string[]) => {
    setFilters((prev) => ({
      ...prev,
      tags: tagIds,
    }));
  };

  const applyFilters = () => {
    setQuery({
      programId: filters.programId === 'all' ? undefined : filters.programId,
      activityType: filters.activityType === 'all' ? undefined : filters.activityType,
      activityStatus: filters.activityStatus === 'all' ? undefined : filters.activityStatus,
      volunteersNeeded: filters.volunteersNeeded ? 'true' : undefined,
      rosterType: filters.rosterType === 'all' ? undefined : filters.rosterType,
      rosterStatus: filters.rosterStatus === 'all' ? undefined : filters.rosterStatus,
      tags: filters.tags.length > 0 ? filters.tags : undefined,
      days: filters.days.length > 0 ? filters.days : undefined,
      pendingApproval: filters.pendingApproval ? 'true' : undefined,
    });

    // Collapse filters after applying
    if (onCollapseFilters) {
      onCollapseFilters();
    }
  };

  const clearFilters = () => {
    const initialFilters = {
      programId: 'all',
      activityType: 'all',
      activityStatus: 'active',
      volunteersNeeded: false,
      rosterType: 'all',
      rosterStatus: 'all',
      tags: [],
      days: [],
      pendingApproval: false,
    };
    setFilters(initialFilters);
    setQuery({
      programId: undefined,
      activityType: undefined,
      activityStatus: undefined,
      volunteersNeeded: undefined,
      rosterType: undefined,
      rosterStatus: undefined,
      tags: undefined,
      days: undefined,
      pendingApproval: undefined,
    });
  };

  return (
    <Collapse
      in={showFilters}
      style={{ marginBottom: showFilters ? '20px' : '0px', marginRight: isMd ? '0px' : '30px' }}
    >
      <Grid
        container
        direction='column'
        style={{
          backgroundColor: theme.color.white.neutral50,
          padding: '0px 10px',
        }}
      >
        <Grid container>
          <Grid item xs={isMobile ? 12 : isXl ? 3 : 6}>
            <FilterItem title='Program'>
              <Select
                style={{ width: '100%' }}
                value={filters.programId}
                onChange={(e) => handleChange({ programId: e.target.value })}
                input={<OutlinedInput margin='dense' />}
              >
                <MenuItem value='all'>All programs</MenuItem>
                {programs
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((program) => (
                    <MenuItem key={program.programId} value={program.programId}>
                      {program.name}
                    </MenuItem>
                  ))}
              </Select>
            </FilterItem>
          </Grid>
          <Grid item xs={isMobile ? 12 : isXl ? 3 : 6}>
            <FilterItem title='Activity Types'>
              <Select
                style={{ width: '100%' }}
                value={filters.activityType}
                onChange={(e) => handleChange({ activityType: e.target.value })}
                input={<OutlinedInput margin='dense' />}
              >
                {activityTypes.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FilterItem>
          </Grid>
          <Grid item xs={isMobile ? 12 : isXl ? 3 : 6}>
            <FilterItem title='Activity Status'>
              <Select
                style={{ width: '100%' }}
                value={filters.activityStatus}
                onChange={(e) => handleChange({ activityStatus: e.target.value })}
                input={<OutlinedInput margin='dense' />}
              >
                {activityStatuses.map((as) => (
                  <MenuItem key={as.id} value={as.id}>
                    {as.name}
                  </MenuItem>
                ))}
              </Select>
            </FilterItem>
          </Grid>
          <Grid item xs={isMobile ? 12 : isXl ? 3 : 6}>
            <FilterItem title='Roster Status'>
              <Select
                style={{ width: '100%' }}
                value={filters.rosterStatus}
                onChange={(e) => handleChange({ rosterStatus: e.target.value })}
                input={<OutlinedInput margin='dense' />}
              >
                {rosterStatuses.map((rs) => (
                  <MenuItem key={rs.id} value={rs.id}>
                    {rs.name}
                  </MenuItem>
                ))}
              </Select>
            </FilterItem>
          </Grid>
          <Grid item xs={isMobile ? 12 : isXl ? 3 : 6}>
            <FilterItem title='Volunteers Needed'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.volunteersNeeded}
                    onChange={(e) => handleChange({ volunteersNeeded: e.target.checked })}
                    color='primary'
                  />
                }
                label='Show only activities needing volunteers'
              />
            </FilterItem>
          </Grid>
          <Grid item lg={3} md={6} sm={6} xs={12}>
            <FilterItem title='Roster Types'>
              <TextField
                name='rosterType'
                value={filters.rosterType}
                select
                variant='outlined'
                fullWidth
                onChange={(e) => handleChange({ rosterType: e.target.value })}
                InputProps={{ classes: inputClasses }}
              >
                {ActivitiesConsts.ROSTERTYPES_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={label} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </FilterItem>
          </Grid>
          <Grid item lg={3} md={6} sm={6} xs={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <FilterItem title='Activity Tags'>
              <ActivityTagSelect
                selectedTagIds={
                  filters.tags?.reduce((acc, tagId) => ({ ...acc, [tagId]: true }), {} as { [key: string]: boolean }) ||
                  {}
                }
                activityTags={activityTagOptions}
                onChange={onSelectActivityTag}
              />
            </FilterItem>
          </Grid>
          <Grid item md={6} sm={6} xs={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <FilterItem title='Day'>
              <DaySelect
                days={ActivitiesConsts.DAY_OPTIONS}
                daysSelected={
                  filters.days?.reduce((acc, day) => ({ ...acc, [day]: true }), {} as { [key: string]: boolean }) || {}
                }
                onSelectDay={onSelectDay}
              />
            </FilterItem>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px', paddingTop: '10px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.pendingApproval}
                  onChange={(e) => handleChange({ pendingApproval: e.target.checked })}
                  style={{ color: '#FF9237' }}
                />
              }
              label='Show only pending approval rosters'
              style={{ color: theme.color.grey.neutral300 }}
            />
          </Grid>
        </Grid>
        <Box display='flex' justifyContent={isMobile ? 'center' : 'flex-start'} p='16px' pl='10px' pb='30px'>
          <TabletButton
            variant='contained'
            size='small'
            style={{
              backgroundColor: '#7047EB',
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              marginRight: '8px',
              minWidth: '40px',
              padding: '7px 12px',
            }}
            onClick={applyFilters}
          >
            SHOW THE RESULTS
          </TabletButton>
          <TabletButton
            variant='contained'
            style={{
              backgroundColor: '#e6e5fc',
              color: theme.color.secondary.main900,
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              minWidth: '40px',
              padding: '7px 12px',
            }}
            onClick={clearFilters}
          >
            CLEAR
          </TabletButton>
        </Box>
      </Grid>
    </Collapse>
  );
}
